/* Ronini shop: filter dropdowns (collection / search toolbar) + search drawer keyboard support.
   Filtering, sorting, predictive search, drawers and add-to-cart stay in theme.js components. */
(() => {
  if (window.rnShop) return;
  window.rnShop = true;

  /* ---------- filter dropdowns: one open at a time, close on outside click / Escape ---------- */
  const closeDropdowns = (except) => {
    document.querySelectorAll('details.rs-dd[open]').forEach((d) => { if (d !== except) d.open = false; });
  };
  document.addEventListener('toggle', (e) => {
    if (e.target.matches?.('details.rs-dd') && e.target.open) closeDropdowns(e.target);
  }, true);
  document.addEventListener('click', (e) => {
    if (!e.target.closest('details.rs-dd')) closeDropdowns();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = e.target.closest?.('details.rs-dd[open]');
    if (open) { open.open = false; open.querySelector('summary').focus(); }
  });

  /* ---------- search drawer ---------- */
  const drawer = () => document.getElementById('search-drawer');
  const input = () => document.getElementById('search-drawer-input');
  const items = () => {
    const ps = document.getElementById('predictive-search');
    if (!ps) return [];
    return [...ps.querySelectorAll('a[href]')].filter((a) => a.getClientRects().length && !a.closest('[hidden]') && a.tabIndex !== -1);
  };

  // Touch devices: the theme skips initial focus, so focus the field once the drawer is shown.
  document.addEventListener('dialog:after-show', (e) => {
    if (e.target === drawer() && document.activeElement !== input()) input()?.focus({ preventScroll: true });
  });

  // Arrow keys: input -> results -> back to input.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const d = drawer();
    if (!d || !d.contains(e.target)) return;
    const list = items();
    if (!list.length) return;
    const field = input();
    const i = list.indexOf(e.target);
    if (e.target === field) {
      if (e.key === 'ArrowDown') { e.preventDefault(); list[0].focus(); }
      return;
    }
    if (i === -1) return;
    e.preventDefault();
    if (e.key === 'ArrowDown') list[Math.min(i + 1, list.length - 1)].focus();
    else if (i === 0) field.focus();
    else list[i - 1].focus();
  });
})();

/* Product-card auto fit: find the product's bounding box in the photo (pixels that aren't the white/transparent
   backdrop), then scale + centre it so the product fills the tile's padded box (the image's own CSS padding in
   rn-shop.css: one value for every product). Runs once per image and on tile resize; images without CORS are
   left as they are (object-fit: contain inside the same padding). */
(() => {
  const SAMPLE = 96;
  const bboxCache = new Map();

  function bbox(img) {
    const key = img.currentSrc || img.src;
    if (bboxCache.has(key)) return bboxCache.get(key);
    const w = SAMPLE, h = Math.max(1, Math.round(SAMPLE * img.naturalHeight / img.naturalWidth));
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    let data;
    try { ctx.drawImage(img, 0, 0, w, h); data = ctx.getImageData(0, 0, w, h).data; } catch (e) { bboxCache.set(key, null); return null; }
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4, a = data[i + 3];
      const light = data[i] > 238 && data[i + 1] > 238 && data[i + 2] > 238;
      if (a > 16 && !light) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    const box = x1 < 0 ? null : { x: x0 / w, y: y0 / h, w: (x1 - x0 + 1) / w, h: (y1 - y0 + 1) / h };
    bboxCache.set(key, box);
    return box;
  }

  function fit(img) {
    if (!img.naturalWidth) return;
    const tile = img.closest('.rs-card__tile'); if (!tile) return;
    const box = bbox(img); if (!box) return;
    const TW = tile.clientWidth, TH = tile.clientHeight, ir = img.naturalWidth / img.naturalHeight;
    const cs = getComputedStyle(img);
    // the padded box the photo is drawn in (object-fit: contain) and the product should fill
    const BW = TW - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight), BH = TH - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    if (!(BW > 0 && BH > 0)) return;
    const dw = Math.min(BW, BH * ir), dh = dw / ir;
    const cw = box.w * dw, ch = box.h * dh;
    const s = Math.min(BH / ch, BW / cw, 4);
    if (!isFinite(s) || s <= 0) return;
    // offset of the product's centre from the photo's centre (the photo is centred in the tile)
    const qx = (box.x + box.w / 2 - 0.5) * dw;
    const qy = (box.y + box.h / 2 - 0.5) * dh;
    img.style.setProperty('--rs-fit', `translate(${(-s * qx).toFixed(1)}px, ${(-s * qy).toFixed(1)}px) scale(${s.toFixed(3)})`);
  }

  function prep(img) {
    if (img.dataset.rsFit) return; img.dataset.rsFit = '1';
    if (!img.crossOrigin) {
      // CORS-enabled request so the pixels can be read (Shopify CDN sends Access-Control-Allow-Origin: *)
      const src = img.getAttribute('src'), srcset = img.getAttribute('srcset');
      img.crossOrigin = 'anonymous';
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
    }
    if (img.complete && img.naturalWidth) fit(img); else img.addEventListener('load', () => fit(img), { once: true });
  }

  const ro = 'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((e) => e.target.querySelectorAll('.rs-card__img').forEach(fit))) : null;
  function scan(root) {
    (root || document).querySelectorAll('.rs-card__img').forEach((img) => { prep(img); const t = img.closest('.rs-card__tile'); if (ro && t && !t.dataset.rsRo) { t.dataset.rsRo = '1'; ro.observe(t); } });
  }
  scan();
  new MutationObserver((m) => m.forEach((r) => r.addedNodes.forEach((n) => { if (n.nodeType === 1) scan(n); }))).observe(document.body, { childList: true, subtree: true });
})();
